import axios, { AxiosError, AxiosResponse } from 'axios';
import { getLogger } from '../diagnostics/logger';
import { getSimManagementErrorFromAxiosError } from '.';
import { QueryResult } from '../models/queryResult';
import {
  BundleAlertDefinition,
  BundleAlertDefinitionInputCreate,
  BundleAlertDefinitionInputUpdate,
} from '../models/transfer/input/BundleAlertDefinition';

import config from '../config';
const log = getLogger('simManagement.api.bundleAlertDefinitionApi');

export function getById(id: string): Promise<BundleAlertDefinition> {
  const url = config.API_BASE_URL + `api/bundleAlertDefinition/${id}`;
  return new Promise<BundleAlertDefinition>((resolve, reject) => {
    axios
      .get(url)
      .then((response: any) => {
        const result = response.data as BundleAlertDefinition;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to load bundle alert definition ${id}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function getByTel(tel: string): Promise<BundleAlertDefinition[]> {
  const url = config.API_BASE_URL + `api/bundleAlertDefinition?tel=${tel}`;
  return new Promise<BundleAlertDefinition[]>((resolve, reject) => {
    axios
      .get(url)
      .then((response: any) => {
        const result = response.data.result;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to load bundle alert definition by tel ${tel}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function getByAccountId(accountId: number, alertType?: number): Promise<BundleAlertDefinition[]> {
  const queryParams = alertType ? '?alertType=' + alertType : '';
  const url = config.API_BASE_URL + `api/account/${accountId}/bundleAlertDefinition${queryParams}`;
  return new Promise<BundleAlertDefinition[]>((resolve, reject) => {
    axios
      .get(url)
      .then((response: AxiosResponse<QueryResult<BundleAlertDefinition>>) => {
        const result = response.data.result;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to get bundle alert definitions for account ${accountId}...`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function get(payload: number | string, alertType?: number): Promise<BundleAlertDefinition[]> {
  if (typeof payload === 'number') {
    const accountId = payload as number;
    return getByAccountId(accountId);
  } else {
    const tel = payload as string;
    return getByTel(tel);
  }
}

/**
 * Creates a bundle alert definition on the server.
 * @param definition The bundle alert definition.
 */
export function create(definition: BundleAlertDefinitionInputCreate) {
  return new Promise<BundleAlertDefinition>((resolve, reject) => {
    axios
      .post(`${config.API_BASE_URL}api/bundleAlertDefinition`, definition)
      .then((response: any) => {
        const result = response.data as BundleAlertDefinition;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to create bundle alert definition.', simManagementError);
        reject(simManagementError);
      });
  });
}

/**
 * Updates an existing bundle alert definition on the server.
 * @param definition The bundle alert definition.
 */
export function update(definition: BundleAlertDefinitionInputUpdate): Promise<BundleAlertDefinition> {
  return new Promise<BundleAlertDefinition>((resolve, reject) => {
    axios
      .put(`${config.API_BASE_URL}api/bundleAlertDefinition/${definition.id}`, definition)
      .then((response: any) => {
        const result = response.data as BundleAlertDefinition;
        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to update bundle alert definition.', simManagementError);
        reject(simManagementError);
      });
  });
}

/**
 * Deletes a bundle alert definition on the server.
 * @param id The identifier of the bundle alert definition to delete.
 */
export function remove(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    axios
      .delete(`${config.API_BASE_URL}api/bundleAlertDefinition/${id}`)
      .then((response: any) => {
        resolve(id);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to delete bundle alert definition ${id}.`, simManagementError);
        reject(simManagementError);
      });
  });
}
